// services/FindMeATenantOrderService.js

import {
  collection,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";

const EMAIL_SERVER_URL = process.env.REACT_APP_EMAIL_SERVER_URL;
const ORDER_AMOUNT = 395;
const CHARGE_DELAY_DAYS = 30;

/**
 * Creates a Find Me A Tenant order in Firestore and notifies the admin/team.
 *
 * The order is written with chargeStatus: "pending" and a chargeDate 30 days
 * out — stripe-server's daily sweep job picks it up and runs the actual
 * charge against the saved card, so nothing here touches Stripe directly.
 *
 * @param {Object} params
 * @param {string} params.uid              - Firebase Auth UID of the owner
 * @param {string} params.email            - owner's email
 * @param {string} params.ownerName        - owner's display name
 * @param {string} params.stripeCustomerId - Stripe customer ID (card now on file)
 * @param {Object} params.propertyInfo     - output of PropertyIntakeForm (incl. infoSource)
 * @returns {Promise<{ orderId: string }>}
 */
export async function createFindMeATenantOrder({
  uid,
  email,
  ownerName,
  stripeCustomerId,
  propertyInfo,
}) {
  if (!uid || !email || !stripeCustomerId) {
    throw new Error(
      "uid, email, and stripeCustomerId are required to create an order."
    );
  }

  const chargeDate = new Date();
  chargeDate.setDate(chargeDate.getDate() + CHARGE_DELAY_DAYS);

  const orderData = {
    uid,
    email,
    ownerName: ownerName || null,
    stripeCustomerId,
    propertyInfo,
    propertyAddress: propertyInfo?.propertyAddress || null,
    infoSource: propertyInfo?.infoSource || "manual",
    amount: ORDER_AMOUNT,
    status: "pending_assignment", // showing agent has not started work yet
    chargeStatus: "pending", // "pending" | "charged" | "failed"
    chargeDate,
    createdAt: serverTimestamp(),
  };

  const docRef = await addDoc(
    collection(db, "findMeATenantOrders"),
    orderData
  );

  // Fire-and-forget admin notification — order creation should not fail
  // just because the notification email had an issue.
  try {
    await fetch(`${EMAIL_SERVER_URL}/api/tenant-emails/find-me-a-tenant-order`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        orderId: docRef.id,
        ownerName,
        ownerEmail: email,
        propertyAddress: orderData.propertyAddress,
        infoSource: orderData.infoSource,
        amount: ORDER_AMOUNT,
        chargeDate: chargeDate.toISOString(),
      }),
    });
  } catch (err) {
    console.error(
      "[FindMeATenantOrderService] Admin notification failed (order still created):",
      err
    );
  }

  return { orderId: docRef.id };
}
